import { YesNoRadioGroupField } from "../../../../../Common/YesNoRadioGroup/YesNoRadioGroupField";
import {
  FormRow,
  FormSection,
  ComputedFieldProps,
  Box,
  Button,
  PlusIcon,
} from "@dbn/omnichannel-design-system-g4p";
import { useFormatMessage } from "../../../../../intl";
import { Option } from "fp-ts/Option";
import { Dispatch, SetStateAction, useMemo } from "react";
import { pipe } from "fp-ts/function";
import { option } from "fp-ts";

import { MoneyField } from "../../../../../Common/Price/MoneyField/MoneyField";
import { useTenantCurrency } from "../../../../../Common/useTenantCurrency";
import { R37Field } from "./Fields/R37Field";
import { R101Field } from "./Fields/R101Field";
import { Tooltip } from "../../../../../Common/Tooltip/Tooltip";
import { InfoSmallIcon } from "@g4p/react-styleguide-library";

type PropsCZ = {
  keepsAccountancyFieldProps: ComputedFieldProps<Option<boolean>>;
  equityPositiveFieldProps: ComputedFieldProps<Option<boolean>>;
  equityFromLastYearFieldProps: ComputedFieldProps<Option<number>>;
  grossIncomesFieldProps: ComputedFieldProps<Option<number>>;
  r37PartialTaxBaseFieldProps: ComputedFieldProps<Option<number>>;
  r101BruttoIncomesFieldProps: ComputedFieldProps<Option<number>>;
  line75ofTaxReturnFieldProps: ComputedFieldProps<Option<number>>;
  hasAdditionalIncome: ComputedFieldProps<Option<boolean>>;
  addInfoFromTaxReturn: boolean;
  setAddInfoFromTaxReturn: Dispatch<SetStateAction<boolean>>;
  isMainIncome: boolean;
  isEditing: boolean;
};

export function IncomeSectionCZ(props: PropsCZ) {
  const formatMessage = useFormatMessage();
  const currency = useTenantCurrency();

  const MIN_MONEY_VALIDATION = -999999999;
  const MAX_MONEY_VALIDATION = 999999999;

  const onAddInfoFromTaxReturn = () => {
    props.setAddInfoFromTaxReturn(true);
  };
  const onCloseAddInfoFromTaxReturnSection = () => {
    props.setAddInfoFromTaxReturn(false);
  };

  const keepAccountancy = useMemo(() => {
    return pipe(props.keepsAccountancyFieldProps.value, option.toNullable);
  }, [props.keepsAccountancyFieldProps]);

  const equityPositive = useMemo(() => {
    return pipe(props.equityPositiveFieldProps.value, option.toNullable);
  }, [props.equityPositiveFieldProps]);

  return (
    <FormSection>
      <FormRow type="full">
        <YesNoRadioGroupField
          {...props.keepsAccountancyFieldProps}
          label={formatMessage(
            "StandardLoan.Tradesman.IncomeSection.isKeepingAccountancy"
          )}
          description={formatMessage(
            "StandardLoan.Tradesman.IncomeSection.isKeepingAccountancyDescription"
          )}
        />
      </FormRow>
      {props.isMainIncome && (
        <FormRow type="full">
          <YesNoRadioGroupField
            {...props.hasAdditionalIncome}
            label={formatMessage("StandardLoan.additionalIncome")}
            description={formatMessage(
              "StandardLoan.additionalIncome.description"
            )}
          />
        </FormRow>
      )}
      {!keepAccountancy && props.addInfoFromTaxReturn ? (
        <FormRow type="full">
          <Box>
            <Button
              label={formatMessage("Cancel")}
              variant="text"
              size="default"
              action={onCloseAddInfoFromTaxReturnSection}
              disabled={!props.isEditing}
            />
          </Box>
        </FormRow>
      ) : null}
      {keepAccountancy && [
        <FormRow type="full">
          <YesNoRadioGroupField
            {...props.equityPositiveFieldProps}
            label={formatMessage(
              "StandardLoan.Tradesman.IncomeSection.isEquityPositiveLabel"
            )}
            description={formatMessage(
              "StandardLoan.Tradesman.IncomeSection.isEquityPositiveDescription"
            )}
          />
        </FormRow>,
        props.addInfoFromTaxReturn && [
          <FormRow type="full">
            <Box>
              <Button
                label={formatMessage("Cancel")}
                variant="text"
                size="default"
                action={onCloseAddInfoFromTaxReturnSection}
                disabled={!props.isEditing}
              />
            </Box>
          </FormRow>,
          props.addInfoFromTaxReturn &&
            equityPositive && [
              <FormRow type="full">
                <MoneyField
                  {...props.equityFromLastYearFieldProps}
                  currency={currency}
                  label={formatMessage(
                    "StandardLoan.Tradesman.IncomeSection.lastYearEquityAmountLabel"
                  )}
                  placeholder={formatMessage(
                    "StandardLoan.Tradesman.IncomeSection.lastYearEquityAmountPlaceholder"
                  )}
                  description={formatMessage(
                    "StandardLoan.Tradesman.IncomeSection.lastYearEquityAmountDescription"
                  )}
                  min={MIN_MONEY_VALIDATION}
                  max={MAX_MONEY_VALIDATION}
                />
              </FormRow>,
              <FormRow type="full">
                <MoneyField
                  {...props.grossIncomesFieldProps}
                  currency={currency}
                  label={formatMessage(
                    "StandardLoan.Tradesman.IncomeSection.grossIncomes"
                  )}
                  placeholder={formatMessage(
                    "StandardLoan.Tradesman.IncomeSection.grossIncomesPlaceholder"
                  )}
                  min={MIN_MONEY_VALIDATION}
                  max={MAX_MONEY_VALIDATION}
                />
              </FormRow>,
              <R37Field
                currency={currency}
                fieldProps={props.r37PartialTaxBaseFieldProps}
              />,
            ],
        ],
      ]}
      {!props.addInfoFromTaxReturn && [
        <FormRow type="full">
          <Box vAlignContent="center">
            <Button
              label={formatMessage(
                "StandardLoan.additionalIncome.addInformationTaxReturn"
              )}
              variant="text"
              action={onAddInfoFromTaxReturn}
              icon={PlusIcon}
              size="default"
              disabled={!props.isEditing}
            />
            <Tooltip
              content={formatMessage(
                "StandardLoan.additionalIncome.addInformationTaxReturn.description"
              )}
            >
              <InfoSmallIcon width={"14px"} height={"14px"} />
            </Tooltip>
          </Box>
        </FormRow>,
      ]}
      {props.addInfoFromTaxReturn &&
        typeof keepAccountancy === "boolean" &&
        !keepAccountancy && [
          <R37Field
            currency={currency}
            fieldProps={props.r37PartialTaxBaseFieldProps}
          />,
          <R101Field
            currency={currency}
            fieldProps={props.r101BruttoIncomesFieldProps}
          />,
        ]}
      {props.addInfoFromTaxReturn && (
        <FormRow type="full">
          <MoneyField
            {...props.line75ofTaxReturnFieldProps}
            currency={currency}
            label={formatMessage(
              "StandardLoan.AdditionalIncome.IncomeSection.line75Label"
            )}
            placeholder={formatMessage(
              "StandardLoan.AdditionalIncome.IncomeSection.line75Placeholder"
            )}
            min={MIN_MONEY_VALIDATION}
            max={MAX_MONEY_VALIDATION}
          />
        </FormRow>
      )}
    </FormSection>
  );
}

type PropsSK = {
  businessGrossIncomeFieldProps: ComputedFieldProps<Option<number>>;
  taxFieldProps: ComputedFieldProps<Option<number>>;
  taxBaseFieldProps: ComputedFieldProps<Option<number>>;
  hasAdditionalIncome: ComputedFieldProps<Option<boolean>>;
  addInfoFromTaxReturn: boolean;
  setAddInfoFromTaxReturn: Dispatch<SetStateAction<boolean>>;
  isMainIncome: boolean;
  isEditing: boolean;
};

export function IncomeSectionSK(props: PropsSK) {
  const formatMessage = useFormatMessage();

  const currency = useTenantCurrency();

  const onAddInfoFromTaxReturn = () => {
    props.setAddInfoFromTaxReturn(true);
  };
  const onCloseAddInfoFromTaxReturnSection = () => {
    props.setAddInfoFromTaxReturn(false);
  };

  return (
    <FormSection>
      {props.isMainIncome && (
        <FormRow type="full">
          <YesNoRadioGroupField
            {...props.hasAdditionalIncome}
            label={formatMessage("StandardLoan.additionalIncome")}
            description={formatMessage(
              "StandardLoan.additionalIncome.description"
            )}
          />
        </FormRow>
      )}
      {!props.addInfoFromTaxReturn && [
        <FormRow type="full">
          <Box vAlignContent="center">
            <Button
              label={formatMessage(
                "StandardLoan.additionalIncome.addInformationTaxReturn"
              )}
              variant="text"
              action={onAddInfoFromTaxReturn}
              icon={PlusIcon}
              size="default"
              disabled={!props.isEditing}
            />
            <Tooltip
              content={formatMessage(
                "StandardLoan.additionalIncome.addInformationTaxReturn.description"
              )}
            >
              <InfoSmallIcon width={"14px"} height={"14px"} />
            </Tooltip>
          </Box>
        </FormRow>,
      ]}
      {props.addInfoFromTaxReturn && [
        <FormRow type="full">
          <Box>
            <Button
              label={formatMessage("Cancel")}
              variant="text"
              size="default"
              action={onCloseAddInfoFromTaxReturnSection}
              disabled={!props.isEditing}
            />
          </Box>
        </FormRow>,
        <FormRow type="full">
          <MoneyField
            {...props.taxBaseFieldProps}
            currency={currency}
            label={formatMessage(
              "StandardLoan.Tradesman.IncomeSection.taxBase"
            )}
            placeholder={formatMessage(
              "StandardLoan.Tradesman.IncomeSection.yearlyAmountPlaceholder"
            )}
            min={-999999999}
            max={999999999}
          />
        </FormRow>,
        <FormRow type="full">
          <MoneyField
            {...props.taxFieldProps}
            currency={currency}
            label={formatMessage("StandardLoan.Tradesman.IncomeSection.tax")}
            placeholder={formatMessage(
              "StandardLoan.Tradesman.IncomeSection.yearlyAmountPlaceholder"
            )}
            min={-999999999}
            max={999999999}
          />
        </FormRow>,
        <FormRow type="full">
          <MoneyField
            {...props.businessGrossIncomeFieldProps}
            currency={currency}
            label={formatMessage(
              "StandardLoan.Tradesman.IncomeSection.businessGrossIncome"
            )}
            placeholder={formatMessage(
              "StandardLoan.Tradesman.IncomeSection.yearlyAmountPlaceholder"
            )}
            min={-999999999}
            max={999999999}
          />
        </FormRow>,
      ]}
    </FormSection>
  );
}
